"use client";
import React, { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styles from './BlogCardSlider.module.css';
import { useTranslation } from 'react-i18next';
import folder from "../../src/assets/icons/folder.svg";
import keyboard from "../../src/assets/icons/keyboard.svg";

const BlogCardSlider = ({ posts }) => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [visibleCards, setVisibleCards] = useState(3); // Default to 3 cards
  const sliderRef = useRef(null);

  // Update visible cards based on screen size
  const updateVisibleCards = () => {
    if (window.matchMedia("(max-width: 768px)").matches) {
      setVisibleCards(1);
    } else if (window.matchMedia("(max-width: 1200px)").matches) {
      setVisibleCards(2);
    } else {
      setVisibleCards(3);
    }
  };

  // Run on mount and resize
  useEffect(() => {
    updateVisibleCards();
    window.addEventListener('resize', updateVisibleCards);
    return () => window.removeEventListener('resize', updateVisibleCards);
  }, []);

  const maxSlides = Math.max(0, posts.length - visibleCards); // Adjust maxSlides based on visible cards

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => Math.max(0, prev - 1));
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => Math.min(maxSlides, prev + 1));
  };

  useEffect(() => {
    if (sliderRef.current) {
      const slideWidth = sliderRef.current.offsetWidth / visibleCards; // Dynamic slide width
      sliderRef.current.style.transform = `translateX(-${currentSlide * slideWidth}px)`;
    }
  }, [currentSlide, visibleCards]);

  return (
    <div className={styles.blogCardSliderContainer}>
      <h2 className={styles.sliderTitle}>{t('Related articles')}</h2>
      
      <div className={styles.sliderWrapper}>
        <button 
          className={`${styles.navButton} ${styles.prevButton} ${currentSlide === 0 ? styles.disabled : ''}`} 
          onClick={handlePrevSlide}
          disabled={currentSlide === 0}
          aria-label={t('Previous slide')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className={styles.cardsContainer}>
          <div ref={sliderRef} className={styles.cardsTrack}>
            {posts.map((post, index) => (
              <div key={index} className={styles.cardWrapper}>
                <div className={styles.blogCard}>
                  <Link href={`/blogs/${post.slug}`} className={styles.imageLink}>
                    <div className={styles.imageContainer}>
                      <Image
                        src={post.image}
                        alt={post.title}
                        className={styles.blogImage}
                      
                        priority
                      />
                    </div>
                  </Link>
                  <div className={styles.contentContainer}>
                    <div className={styles.metaInfo}>
                      <div className={styles.metaItem}>
                        <Image src={keyboard} alt="Author" height={20} width={20} style={{marginBottom:"5px"}}/>
                        <Link 
                          href={`/blogs/author/keychoice/`} 
                          className={styles.metaLink}
                        >
                          {post.author || "Matthew Clark"}
                        </Link>
                      </div>
                      
                      <div className={styles.metaItem}>
                        <Image src={folder} alt="Category" width={16} height={16} />
                        <Link
                          href={`/blogs/category/${post.relatedCategory}`} 
                          className={styles.metaLink}
                        >
                          {post.category}
                        </Link>
                      </div>
                    </div>
                    
                    <Link href={`/blogs/${post.slug}`} className={styles.titleLink}>
                      <h3 className={styles.blogTitle}>{post.title}</h3>
                    </Link>
                    
                    <Link href={`/blogs/${post.slug}`} className={styles.readMoreLink}>
                      <span className={styles.readMore}>{t("Read more")}</span>
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 5H9M9 5L5 1M9 5L5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button 
          className={`${styles.navButton} ${styles.nextButton} ${currentSlide >= maxSlides ? styles.disabled : ''}`} 
          onClick={handleNextSlide}
          disabled={currentSlide >= maxSlides}
          aria-label={t('Next slide')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default BlogCardSlider;